import { FC, MouseEvent, useCallback, useMemo } from 'react';

import { Product } from 'models/product';
import { truncate } from 'utils/stringUtils';
import { useGetProductOfferingsQuery } from 'store/productService';
import { pushGTMEvent } from 'utils/analyticsUtils';
import { useTranslations } from 'hooks/useTranslations';
import * as config from 'config';

import Button from 'components/UI/Buttons/Button';
import Loader from 'components/UI/Loader';

import {
  Card,
  CardHeader,
  CardActions,
  CardTitleCategory,
  CardImageContainer,
  CardData,
  CardTitleModelName,
  CardTitleDescription
} from './styled';

type Props = {
  product: Product;
  husqvarnaId?: string;
  showOfferings?: boolean;
  onClick?: () => void;
};

const ProductCard: FC<Props> = ({
  product: {
    iprId,
    productCategory,
    modelName,
    description,
    imageUrl,
    pnc = '–',
    serialNumber = '–'
  },
  husqvarnaId,
  showOfferings = false,
  onClick
}) => {
  const t = useTranslations();

  // Get product offerings
  const { data, isLoading } = useGetProductOfferingsQuery(
    { iprId },
    { skip: !showOfferings }
  );

  // Redirect to offer
  const onOfferClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      pushGTMEvent('productOfferClick', {
        productId: iprId
      });
      window.open(
        `${config.keylightUrl}/checkout?iprId=${iprId}&orderKey=${config.keylightOrderKey}`
      );
    },
    [iprId]
  );

  // Handle card click
  const handleCardClick = useCallback(() => {
    pushGTMEvent('productCardClick', {
      productId: iprId,
      productCategory
    });
    onClick?.();
  }, [onClick, iprId, productCategory]);

  // Render card data
  const cardData = useMemo(() => {
    if (husqvarnaId) {
      return [
        {
          id: 1,
          title: t('productinfo.hid'),
          value: husqvarnaId
        }
      ];
    }

    return [
      {
        id: 1,
        title: t('productinfo.product_number'),
        value: pnc
      },
      {
        id: 2,
        title: t('productinfo.serial_number'),
        value: serialNumber
      }
    ];
  }, [husqvarnaId, pnc, serialNumber, t]);

  return (
    <Card onClick={handleCardClick} data-testid="product-card" $noSelect>
      {imageUrl && (
        <CardImageContainer>
          <img src={imageUrl} alt={modelName} />
        </CardImageContainer>
      )}
      <CardHeader>
        <div>
          <CardTitleCategory>
            {productCategory?.toLowerCase()}
          </CardTitleCategory>
          <CardTitleModelName>{modelName}</CardTitleModelName>
          <CardTitleDescription>{truncate(description)}</CardTitleDescription>
        </div>
      </CardHeader>
      <CardData>
        {cardData.map((item) => (
          <div key={item.id}>
            <h6>{item.value}</h6>
            <p>{item.title}</p>
          </div>
        ))}
      </CardData>
      {showOfferings && (
        <CardActions>
          {data?.offerings && (
            <Button
              size="medium"
              color={config.isConstruction ? 'secondary' : 'success'}
              onClick={onOfferClick}
            >
              {t('myproducts.btn_offers')}
            </Button>
          )}
          {isLoading && (
            <div>
              <Loader size="small" />
            </div>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default ProductCard;
